import { Injectable } from '@angular/core';
import { Events } from 'ionic-angular';
import { Cookie } from 'ng2-cookies/ng2-cookies';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Storage } from '@ionic/storage';
import { County, FeatureLayerDefinition, Layer, XandY } from '../interfaces';

@Injectable()
export class AppData {

	acceptedDisclaimer: boolean = false;
	Counties: Array<County>;
	selectedBasemap: string;
	selectedFeature: any;
	selectedRoadwayLayer: FeatureLayerDefinition;
	message: string;
    DynamicSubLayers: Array<Layer>;
    RoadLayers: Array<FeatureLayerDefinition>;
    OtherLayers: Array<FeatureLayerDefinition>;
	CombinedFeatureLayers: Array<FeatureLayerDefinition>;
	devicePosition: Position;
	devicePositionSubject: BehaviorSubject<any>;
	positionErrorText: string = '';
	settings: any;
	geolocationOptions: any = {
		enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: Infinity
	};

    constructor(
        public events: Events,
        public storage: Storage
    ) {
		this.initializePositionWatcher();
		this.readDisclaimerCookie();
		this.initializeConfigSettings();
		this.initializeDynamicLayers();
		this.initializeFeatureLayers();
    }

	public getSelectedLayerName() {
		if (this.selectedFeature && this.selectedFeature.layerId) {
			return this.DynamicSubLayers[this.selectedFeature.layerId].title;
		}
		return '';
	}

	public getCurrentPositionOrDefault(): XandY {
		if (this.devicePosition) {
			let coords = (<any>this.devicePosition).coords;
			return {'x': coords.longitude, 'y':coords.latitude};
		} else {
			return this.settings.defaultMapCoords;
		}
	}

	public getOutFieldsFromLayerDefinition(layer: FeatureLayerDefinition) {
		let outFields: Array<string> = [];
		layer.fieldsAndAliases.forEach(item => {
			outFields.push(item.split('|')[0]);
		});
		return outFields;
	}
	public getFieldAliasDictionary(layer: FeatureLayerDefinition) {
		let aliasDictionary = {};
		layer.fieldsAndAliases.forEach(item => {
			let fieldAndAlias = item.split('|');
			var field = fieldAndAlias[0];
			var alias = field;
			if (fieldAndAlias.length > 1) {
				alias = fieldAndAlias[1];
			}
			aliasDictionary[field] = alias;
		});
		return aliasDictionary;
    }

    public getEnhancedValue(layer: FeatureLayerDefinition, field: string, value: any) {
        // Lat/Long truncation
        if (field === 'FN_LATITUDE' || field === 'FN_LONGITUDE') {
            return parseFloat(value.toString()).toFixed(5);
        }
        // Value descriptions
        if (layer.fieldValueDescriptions) {
            let fieldDescriptionsArray = layer.fieldValueDescriptions.filter(x => { return x.field === field;});
            if (fieldDescriptionsArray.length > 0) {
                let fieldDescriptionsObject = fieldDescriptionsArray[0];
                fieldDescriptionsObject.valuesAndDescriptions.forEach(x => {
                    let valueAndDescription = x.split('|');
                    if (valueAndDescription.length > 1) {
                        let matchingValue = valueAndDescription[0];
                        let description = valueAndDescription[1];
                        if (value && matchingValue === value) {
                            value += ' - ' + description;
                        }
                    }
                });
            }
        }
        return value;
	}

	public resetDevicePosition(): Promise<any> {
		return new Promise((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(p => {
				this.devicePosition = p;
				resolve(p);
			}, error => {
				this.setGeolocationError(error);
				reject();
			}, this.geolocationOptions);
		});
	}

	private initializePositionWatcher() {
		this.devicePositionSubject = new BehaviorSubject(null);

        navigator.geolocation.watchPosition(p => {
			this.devicePosition = p;
			this.devicePositionSubject.next(p);
        }, error => {
			this.devicePositionSubject.next(null);
            this.setGeolocationError(error);
        }, this.geolocationOptions);
	}

	private setGeolocationError(error) {
		switch (error.code) {
			case error.PERMISSION_DENIED:
				this.positionErrorText = 'Geolocation request denied.';
				break;
			case error.POSITION_UNAVAILABLE:
				this.positionErrorText = 'Location information is unavailable.';
				break;
			case error.TIMEOUT:
				this.positionErrorText = 'Unable to start geolocation. Check device settings.';
				break;
		}
	}

	private readDisclaimerCookie() {
        let cookie = Cookie.get('tire_disclaimer_accepted');
		if (cookie) {
			this.acceptedDisclaimer = true;
		}
	}

    private initializeConfigSettings() {
        this.settings = (<any>window).settings;
		this.message = '';
		this.selectedBasemap = this.settings.defaultBasemap;
	}

	private initializeDynamicLayers() {
		this.DynamicSubLayers = [];
		if (this.settings.layerMode === 'dynamic') {
			this.settings.dynamicLayers.forEach(dynamicLayer => {
				dynamicLayer.layerId = this.DynamicSubLayers.length;
				this.DynamicSubLayers.push(dynamicLayer);
			});
		}
	}

	private initializeFeatureLayers() {
		this.RoadLayers = this.settings.layerDefinitions.roadLayers;
		this.OtherLayers = this.settings.layerDefinitions.otherLayers;
        this.CombinedFeatureLayers = this.RoadLayers.concat(this.OtherLayers);
        this.selectedRoadwayLayer = this.CombinedFeatureLayers[0];
		if (this.settings.layerMode === 'dynamic') {
			this.CombinedFeatureLayers.length = 0;
		} else {
			this.DynamicSubLayers.length = 0;
		}
	}

}


// WEBPACK FOOTER //
// ./src/providers/app-data.ts